import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import api from '../services/api.jsx';
import PolicyReviewPanel from './PolicyReviewPanel.jsx';
import { downloadPolicyReviewExcel, normalizePolicyReview } from './policyReview.js';

const formatDate = value => (value ? new Date(value).toLocaleString() : '—');
const formatCount = value => (value == null ? '—' : Number(value).toLocaleString());

const PolicyReviewAdminTab = () => {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ enabled: true, isPaused: false, runDayOfMonth: 1, runHourUtc: 3, runMinuteUtc: 0 });
  const [scheduleMessage, setScheduleMessage] = useState('');
  const [scheduleError, setScheduleError] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState('');
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState('');
  const normalized = useMemo(() => (result?.data == null ? null : normalizePolicyReview(result.data)), [result]);

  const loadStatus = async () => {
    try {
      const res = await api.get('/admin/zoho/policy-review/schedule');
      setStatus(res.data);
      setForm({
        enabled: res.data.enabled,
        isPaused: res.data.isPaused,
        runDayOfMonth: res.data.runDayOfMonth,
        runHourUtc: res.data.runHourUtc,
        runMinuteUtc: res.data.runMinuteUtc
      });
    } catch (err) {
      setScheduleError(err.response?.data?.error || 'Failed to load policy review schedule');
    }
  };

  const loadRuns = async () => {
    setRunsLoading(true);
    setRunsError('');
    try {
      const res = await api.get('/admin/zoho/policy-review/runs?limit=50');
      setRuns(Array.isArray(res.data?.runs) ? res.data.runs : []);
    } catch (err) {
      setRunsError(err.response?.data?.error || 'Failed to load policy review run history');
    } finally {
      setRunsLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    loadRuns();
  }, []);

  const saveSchedule = async () => {
    setSavingSchedule(true);
    setScheduleMessage('');
    setScheduleError('');
    try {
      const res = await api.put('/admin/zoho/policy-review/schedule', form);
      setStatus(res.data);
      setScheduleMessage('Schedule saved.');
    } catch (err) {
      setScheduleError(err.response?.data?.error || 'Failed to save policy review schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setRunError('');
    setResult(null);
    try {
      const res = await api.post('/admin/zoho/monthly-maintenance/policy-review');
      setResult({ runId: res.data?.runId, data: res.data?.data ?? null, warning: res.data?.warning || '' });
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to run SDP policy review';
      const correlationId = err.response?.data?.correlationId;
      setRunError(correlationId ? `${message} (Correlation ID: ${correlationId})` : message);
    } finally {
      setRunning(false);
      loadRuns();
      loadStatus();
    }
  };

  const updateForm = (key, value) => setForm(current => ({ ...current, [key]: value }));

  return (
    <>
      <div className="admin-jobdiva-card">
        <h3>Policy Review Schedule</h3>
        {status && (
          <div className="admin-jobdiva-status-grid" style={{ marginBottom: 12 }}>
            <div><strong>Next Run:</strong> {status.nextRunAt ? formatDate(status.nextRunAt) : 'Not scheduled'}</div>
            <div><strong>Last Run:</strong> {status.lastRun ? `${formatDate(status.lastRun.startedAt)} (${status.lastRun.status})` : 'Never'}</div>
            <div><strong>Running:</strong> {status.isRunning ? 'Yes' : 'No'}</div>
            {!status.workerEnabled && <div><strong>Worker:</strong> Disabled by server configuration</div>}
          </div>
        )}
        <div className="admin-zoho-maintenance-form">
          <label>
            <span>Enabled</span>
            <input type="checkbox" checked={form.enabled} onChange={e => updateForm('enabled', e.target.checked)} />
          </label>
          <label>
            <span>Paused</span>
            <input type="checkbox" checked={form.isPaused} onChange={e => updateForm('isPaused', e.target.checked)} />
          </label>
          <label>
            <span>Day Of Month</span>
            <input type="number" min="1" max="31" value={form.runDayOfMonth} onChange={e => updateForm('runDayOfMonth', e.target.value)} />
          </label>
          <label>
            <span>Hour UTC</span>
            <input type="number" min="0" max="23" value={form.runHourUtc} onChange={e => updateForm('runHourUtc', e.target.value)} />
          </label>
          <label>
            <span>Minute UTC</span>
            <input type="number" min="0" max="59" value={form.runMinuteUtc} onChange={e => updateForm('runMinuteUtc', e.target.value)} />
          </label>
        </div>
        <div style={{ marginTop: 12 }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={saveSchedule} disabled={savingSchedule}>
            Save Schedule
          </button>
        </div>
        {scheduleMessage && <div style={{ color: '#107c10', marginTop: 8 }}>{scheduleMessage}</div>}
        {scheduleError && <div role="alert" style={{ color: 'red', marginTop: 8 }}>{scheduleError}</div>}
      </div>

      <div className="admin-jobdiva-card">
        <div className="admin-jobdiva-table-toolbar">
          <h3>Run History</h3>
          <button type="button" className="btn btn-secondary btn-sm" onClick={loadRuns} disabled={runsLoading}>Refresh</button>
          <button type="button" onClick={runNow} disabled={running}>
            {running ? 'Running...' : 'Run Policy Review Now'}
          </button>
        </div>
        {runsError && <div role="alert">{runsError}</div>}
        {runsLoading ? (
          <div>Loading history...</div>
        ) : runs.length > 0 ? (
          <div className="admin-jobdiva-table-wrap">
            <table className="admin-jobdiva-table">
              <thead>
                <tr>
                  <th>Run Date</th>
                  <th>Trigger</th>
                  <th>Status</th>
                  <th>Critical CVEs</th>
                  <th>High CVEs</th>
                  <th>OneDrive Accounts</th>
                  <th>Sync Error Devices</th>
                  <th>Mailboxes Due</th>
                  <th>Error</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {runs.map(run => (
                  <tr key={run.runId}>
                    <td>{formatDate(run.startedAt)}</td>
                    <td>{run.triggerSource}{run.triggeredBy ? ` (${run.triggeredBy})` : ''}</td>
                    <td>{run.status}</td>
                    <td>{formatCount(run.counts?.defenderCritical)}</td>
                    <td>{formatCount(run.counts?.defenderHigh)}</td>
                    <td>{formatCount(run.counts?.oneDriveAccounts)}</td>
                    <td>{formatCount(run.counts?.oneDriveSyncErrorDevices)}</td>
                    <td>{formatCount(run.counts?.exchangeDueMailboxes)}</td>
                    <td>{run.errorMessage || '—'}</td>
                    <td>{run.hasFullData && <Link to={`/admin/policy-review/${run.runId}`}>View</Link>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div>No policy review runs recorded yet.</div>
        )}
      </div>

      {(running || runError || result) && (
        <div className="admin-jobdiva-card">
          <div className="admin-jobdiva-table-toolbar">
            <h3>Latest Ad-hoc Result</h3>
            {normalized && (
              <button type="button" onClick={() => downloadPolicyReviewExcel(normalized)}>
                <Download size={16} aria-hidden="true" /> Download to Excel
              </button>
            )}
          </div>
          {running && <div>Running policy review...</div>}
          {runError && <div role="alert">{runError}</div>}
          {result?.warning && <div role="alert">{result.warning}</div>}
          {result && !normalized && <div>No policy review results returned.</div>}
          {normalized && <PolicyReviewPanel data={normalized} />}
        </div>
      )}
    </>
  );
};

export default PolicyReviewAdminTab;
